import React, { useEffect, useState } from "react";
import {
  TextField,
  CircularProgress,
  Modal,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import "./CreateAgentEmployee.scss";
import CountryCodeMenuItem from "../countryCodeMenuItem/CountryCodeMenuItem";
import AgentProfilePicture from "./AgentProfilePicture";
import ErrorSummary from "./ErrorSummary";
import { getCdnImageUrl, getImageUrl } from "../../../../utils/helpers/common";
import { LocalStorageHelpers } from "../../../../utils/helpers/localstorage.helpers";
import SearchIcon from "../../../../components/icons/SearchIcon";
import CloseIcon from "../../../../components/icons/CloseIcon";
import { AgentEmployeeApi } from "../../../../utils/api/AgentEmployeeApi";
import { clientRoutes } from "../../../../utils/routes";

const CreateAgentEmployee = () => {
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [managerSearch, setManagerSearch] = useState("");
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerList, setManagerList] = useState<any[]>([]);

  const initialValues = {
    first_name: "",
    last_name: "",
    email: "",
    mobile_country_code: "",
    mobile_number: "",
    designation: "",
    reporting_manager_id: "",
    reporting_manager_name: "",
    profileImageLive: "",
    profileImageUploadRecord: null,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      first_name: Yup.string().required("is required!"),
      last_name: Yup.string().required("is required!"),
      email: Yup.string().email("Enter valid email!").required("is required!"),
      mobile_country_code: Yup.string().required("is required!"),
      mobile_number: Yup.string().required("is required!"),
      reporting_manager_id: Yup.string().required("is required!"),
      profileImageLive: Yup.string().required("is required!"),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      setIsLoading(true);

      try {
        const formData = new FormData();

        formData.append("email", values.email);
        formData.append("first_name", values.first_name);
        formData.append("last_name", values.last_name);

        formData.append(
          "mobile_country_code",
          (values.mobile_country_code || "").replace("+", ""),
        );
        formData.append("mobile_number", values.mobile_number);

        if (values.designation)
          formData.append("designation", values.designation);

        formData.append("reporting_manager_id", values.reporting_manager_id);

        if (profileImage) formData.append("profile_photo", profileImage);

        const res = await AgentEmployeeApi.create(formData);

        if (res.success && res.data?.success) {
          toast.success("Employee created successfully");
          navigate(clientRoutes.agentEmployeeList);
        } else {
          toast.error(res?.error || res?.data?.message || "Failed to create");
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  const openReportingManagerModal = () => {
    setIsManagerModalOpen(true);
  };

  const getAgencyId = () => {
    const getData: any =
      LocalStorageHelpers?.getAgentData() ||
      LocalStorageHelpers?.getAgencyData();

    if (!getData) return "";

    if (getData?.role === "agent") return getData?.agency_ref?._id || "";
    return getData?._id || "";
  };

  const getProfileUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.medium_image_url)
      return getCdnImageUrl(uploadRecord.medium_image_url);
    if (uploadRecord.small_image_url)
      return getCdnImageUrl(uploadRecord.small_image_url);
    if (uploadRecord.storage_key)
      return getCdnImageUrl(uploadRecord.storage_key);
    if (uploadRecord.compressed_file_path)
      return getImageUrl(uploadRecord.compressed_file_path);
    if (uploadRecord.original_file_path)
      return getImageUrl(uploadRecord.original_file_path);

    return "";
  };

  const mapAgentToManager = (a: any) => ({
    id: a?._id,
    role: "agent",
    name:
      a?.fullname?.trim() ||
      `${a?.principal_agent_first_name || ""} ${a?.principal_agent_last_name || ""}`.trim() ||
      "-",
    email: a?.email || "-",
    avatar: getProfileUrl(a?.profile_image_ref),
    uploadRecord: a?.profile_image_ref || null,
  });

  const mapAgentEmployeeToManager = (e: any) => ({
    id: e?._id,
    role: "agent_employee",
    name:
      e?.full_name?.trim() ||
      `${e?.first_name || ""} ${e?.last_name || ""}`.trim() ||
      "-",
    email: e?.email || "-",
    avatar: getProfileUrl(e?.profile_photo),
    uploadRecord: e?.profile_photo || null,
  });

  const fetchManagers = async (searchText: string) => {
    setManagerLoading(true);
    try {
      const res = await AgentEmployeeApi.listReportingManagers({
        search: searchText,
        limit: 50,
        include_agency_employees: true,
      });

      if (res?.success && res?.data?.success) {
        const docs = res.data.data.docs || [];
        const mapped = docs.map((x: any) => ({
          ...x,
          avatar: getProfileUrl(x.uploadRecord),
        }));
        setManagerList(mapped);
      } else {
        setManagerList([]);
      }
    } catch (e) {
      setManagerList([]);
    } finally {
      setManagerLoading(false);
    }
  };

  useEffect(() => {
    if (!isManagerModalOpen) return;
    fetchManagers(managerSearch);
  }, [isManagerModalOpen]);

  useEffect(() => {
    if (!isManagerModalOpen) return;

    const t = setTimeout(() => {
      fetchManagers(managerSearch);
    }, 400);

    return () => clearTimeout(t);
  }, [managerSearch, isManagerModalOpen]);

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="create-agency-employee-wrapper">
          <div
            className="propertie-view-back-btn"
            onClick={() => navigate(clientRoutes.agentEmployeeList)}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to list</p>
          </div>

          <div className="page-header">
            <h2>Create Agent Employee</h2>
          </div>

          <div className="form-content-wrapper">
            <div className="section-header">
              <h3>Employee Info</h3>
              <p className="subtitle">
                Provide primary information for the new employee
              </p>
            </div>

            <form className="form-container" onSubmit={formik.handleSubmit}>
              <div className="double-item-container">
                <div className="input-and-lable-container">
                  <p className="label font-inter-400">First name*</p>
                  <TextField
                    placeholder="Enter first name"
                    variant="outlined"
                    fullWidth
                    className="gray-bg-input"
                    name="first_name"
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    error={Boolean((formik.errors as any).first_name)}
                  />
                </div>

                <div className="input-and-lable-container">
                  <p className="label font-inter-400">Last name*</p>
                  <TextField
                    placeholder="Enter last name"
                    variant="outlined"
                    fullWidth
                    className="gray-bg-input"
                    name="last_name"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    error={Boolean((formik.errors as any).last_name)}
                  />
                </div>
              </div>

              <div className="double-item-container">
                <div className="input-and-lable-container">
                  <p className="label font-inter-400">Email*</p>
                  <TextField
                    placeholder="Enter email"
                    variant="outlined"
                    fullWidth
                    className="gray-bg-input"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={Boolean((formik.errors as any).email)}
                  />
                </div>

                <div className="input-and-lable-container">
                  <p className="label font-inter-400">Mobile*</p>
                  <div className="unified-phone-input">
                    <div className="code-picker">
                      <CountryCodeMenuItem
                        formik={formik}
                        name="mobile_country_code"
                        disabled={false}
                      />
                    </div>

                    <TextField
                      placeholder="Enter mobile"
                      variant="outlined"
                      className="mobile-input-internal"
                      name="mobile_number"
                      value={formik.values.mobile_number}
                      onChange={formik.handleChange}
                      error={Boolean((formik.errors as any).mobile_number)}
                    />
                  </div>
                </div>
              </div>

              <div className="double-item-container">
                <div className="input-and-lable-container">
                  <p className="label font-inter-400">Reporting manager*</p>

                  <FormControl
                    variant="outlined"
                    className={`reporting-manager-select ${
                      (formik.errors as any).reporting_manager_id
                        ? "input-error"
                        : ""
                    }`}
                  >
                    <Select
                      displayEmpty
                      value={
                        formik.values.reporting_manager_name ? "selected" : ""
                      }
                      IconComponent={KeyboardArrowDownIcon}
                      open={false}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        openReportingManagerModal();
                      }}
                      renderValue={() => (
                        <div className="rm-select-value">
                          <div className="rm-search-icon">
                            <SearchIcon />
                          </div>

                          <span
                            className={`rm-text ${
                              formik.values.reporting_manager_name
                                ? "filled"
                                : ""
                            }`}
                          >
                            {formik.values.reporting_manager_name ||
                              "Select reporting manager"}
                          </span>
                        </div>
                      )}
                      MenuProps={{
                        disablePortal: true,
                        classes: { paper: "selector-menu-container" },
                      }}
                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="selected"></MenuItem>
                    </Select>
                  </FormControl>
                </div>

                <div className="input-and-lable-container">
                  <p className="label font-inter-400">Designation</p>
                  <TextField
                    placeholder="Enter designation"
                    variant="outlined"
                    fullWidth
                    className="gray-bg-input"
                    name="designation"
                    value={formik.values.designation}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>

              <div className="photo-upload-section">
                <h3 className="section-title">Profile Photo</h3>
                <p className="section-subtitle">
                  Add a photo for the new employee
                </p>

                <div className="photo-box-wrapper">
                  <AgentProfilePicture
                    profileImage={profileImage}
                    setProfileImage={setProfileImage}
                    formik={formik}
                  />
                </div>
              </div>

              {Object.keys(formik.errors).length > 0 && (
                <ErrorSummary errors={formik.errors as any} />
              )}

              <div className="action-buttons">
                <button
                  type="button"
                  className="cancel-btn font-inter-400"
                  onClick={() => navigate(clientRoutes.agentEmployeeList)}
                >
                  Cancel
                </button>

                <button
                  className="create-btn font-inter-500"
                  disabled={isLoading}
                  type="submit"
                >
                  {isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>
          </div>

          <Modal
            open={isLoading}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") setIsLoading(false);
            }}
            aria-labelledby="processing-modal"
            className="processing-modal agency-employee-create-modal"
            disableEscapeKeyDown
          >
            <div className="modal-center-wrapper">
              <div className="loading-content-wrapper">
                <div className="loading-container">
                  <div className="spinner" />
                  <p className="loading-text">
                    Processing your request
                    <span className="desktop-break">
                      <br />
                    </span>
                    Please wait...
                  </p>
                </div>
              </div>
            </div>
          </Modal>

          <Modal
            open={isManagerModalOpen}
            onClose={() => setIsManagerModalOpen(false)}
            className="reporting-manager-modal"
          >
            <div className="rm-modal-center">
              <div className="rm-modal-card">
                <div className="rm-modal-header">
                  <p className="rm-title">Select reporting manager</p>

                  <button
                    className="rm-close"
                    onClick={() => setIsManagerModalOpen(false)}
                  >
                    <CloseIcon />
                  </button>
                </div>

                <div className="rm-search">
                  <div className="rm-search-icon">
                    <SearchIcon />
                  </div>
                  <input
                    placeholder="Search by name or email"
                    value={managerSearch}
                    onChange={(e) => setManagerSearch(e.target.value)}
                  />
                </div>

                <div className="rm-list">
                  {managerLoading ? (
                    <div className="rm-loading">
                      <CircularProgress size={20} />
                    </div>
                  ) : managerList.length === 0 ? (
                    <div className="rm-empty">No results</div>
                  ) : (
                    managerList.map((item) => (
                      <div
                        key={item.id}
                        className="rm-row"
                        onClick={() => {
                          formik.setFieldValue("reporting_manager_id", item.id);
                          formik.setFieldValue(
                            "reporting_manager_role",
                            "agent_employee",
                          );
                          formik.setFieldValue(
                            "reporting_manager_name",
                            item.name,
                          );
                          setIsManagerModalOpen(false);
                        }}
                      >
                        <div className="rm-avatar">
                          {item.avatar ? (
                            <img src={item.avatar} alt="avatar" />
                          ) : (
                            <div className="rm-avatar-fallback">
                              {(item.name || "U")[0]?.toUpperCase()}
                            </div>
                          )}
                        </div>

                        <div className="rm-info">
                          <div className="rm-name">{item.name}</div>
                          <div className="rm-email">{item.email}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </Modal>
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default CreateAgentEmployee;
